import { css } from '@emotion/react'
import {
  Heading,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'
import { type AlertProps } from '@livechat/design-system-react-components/dist/components/Alert/Alert'

import textLogoMark from '@/assets/icons/text-logo-mark-white-bg.svg'
import { getDesignationLabel, isOwnedByText } from '@/utils/products'
import { sendEmail } from '@/utils/browser'
import InfoAlert from '../InfoAlert'

const infoAlertCss = css`
  button:first-of-type {
    padding-left: var(${SpacingToken.Spacing9});
    white-space: nowrap;

    ::before {
      display: block;
      content: url(${textLogoMark});
      transform: scale(0.25);
      position: absolute;
      left: -22px;
      top: -25px;
    }
  }
`

const titleCss = css`
  && {
    margin-bottom: var(${SpacingToken.Spacing2});
  }
`

interface Props {
  name: string
  tutorialUrl?: string
  contactEmail?: string
  company?: string
  designation: string
  ownerId: string
}

const TutorialBanner = ({
  name,
  tutorialUrl,
  contactEmail,
  company,
  designation,
  ownerId,
}: Props) => {
  if (!tutorialUrl && !(company && contactEmail)) {
    return null
  }

  const isAppOwnerText = isOwnedByText(ownerId)
  const designationLabel = getDesignationLabel(designation)
  const hasCompanyInfo = company && contactEmail
  const contactLabel = isAppOwnerText ? 'Contact Text' : `Contact ${company}`

  const appTutorial: AlertProps['primaryButton'] = {
    handleClick: () =>
      window.open(tutorialUrl, '_blank', 'noopener noreferrer'),
    label: 'App tutorial',
    id: 'app-tutorial-button',
  }
  const contactDeveloper: AlertProps['secondaryButton'] = {
    handleClick: () => {
      sendEmail(contactEmail)
    },
    label: contactLabel,
    id: 'contact-developer-button',
  }

  const primaryButton = tutorialUrl ? appTutorial : contactDeveloper
  const secondaryButton =
    tutorialUrl && hasCompanyInfo ? contactDeveloper : undefined

  return (
    <InfoAlert
      data-test="app-tutorial-alert"
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      css={[isAppOwnerText && !tutorialUrl && infoAlertCss]}
    >
      <Heading size="xs" css={titleCss} data-test="app-tutorial-header">
        How to use this app
      </Heading>
      <Text data-test="app-tutorial-text">
        {tutorialUrl &&
          `Follow the app tutorial with setup instructions on how to use and properly configure this app with your ${designationLabel} account.`}{' '}
        {hasCompanyInfo &&
          `To get help and support contact ${company}. You can also suggest improvements or request new features in the upcoming versions of ${name}.`}
      </Text>
    </InfoAlert>
  )
}

export default TutorialBanner
